import { ChevronDownIcon, ChevronUpIcon, Cross2Icon } from "@radix-ui/react-icons";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useJobPolling } from "../../hooks/useJobPolling";
import { JobStatusItem } from "../JobStatusItem";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils/common";
import {
  selectHasAnyJobs,
  selectJobs,
  selectMenuState,
  setMenuState,
  selectHasActiveJobs,
  clearAllJobs,
} from "@/store/asyncJobs";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

export function Menu() {
  const dispatch = useAppDispatch();
  const menuState = useAppSelector(selectMenuState);
  const hasJobs = useAppSelector(selectHasAnyJobs);
  const hasActiveJobs = useAppSelector(selectHasActiveJobs);
  const jobs = useAppSelector(selectJobs);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  useJobPolling();

  useEffect(() => {
    const container = document.createElement("div");
    container.id = "async-job-menu-portal";
    document.body.appendChild(container);
    setPortalContainer(container);

    return () => {
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    };
  }, []);

  const toggleExpanded = () => {
    dispatch(setMenuState(menuState === "expanded" ? "collapsed" : "expanded"));
  };

  const cancelDownloads = () => {
    dispatch(clearAllJobs());
    dispatch(setMenuState("closed"));
  };

  const handleCloseMenu = () => {
    if (hasActiveJobs) {
      setIsConfirmDialogOpen(true);
    } else {
      cancelDownloads();
    }
  };

  const handleConfirmCancel = () => {
    setIsConfirmDialogOpen(false);
    cancelDownloads();
  };

  const handleContinueDownload = () => {
    setIsConfirmDialogOpen(false);
  };

  if (!hasJobs || !portalContainer || menuState === "closed") {
    return null;
  }

  return createPortal(
    <div
      className={cn(
        "fixed bottom-0 right-0 w-96 max-w-full z-50 bg-background border border-border rounded-t-lg shadow-lg transition-all duration-200 ease-in-out print:hidden",
        menuState === "expanded" ? "max-h-96" : "max-h-14",
      )}
      aria-live="polite"
      role="region"
      aria-label="Job status notifications"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
        <div className="flex items-center gap-2 flex-1">
          <h3 className="font-medium flex items-center gap-2">
            <span>Active Jobs</span>
            <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs">
              {jobs.length}
            </span>
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={toggleExpanded} className="w-7 h-7" variant={"ghost"}>
            {menuState === "expanded" ? <ChevronDownIcon /> : <ChevronUpIcon />}
            <span className="sr-only">{menuState === "expanded" ? "Collapse" : "Expand"}</span>
          </Button>
          <AlertDialog open={isConfirmDialogOpen}>
            <Button variant={"ghost"} className="h-7 w-7" onClick={handleCloseMenu}>
              <Cross2Icon className="h-4 w-4" />
              <span className="sr-only">Close menu</span>
            </Button>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Cancel download?</AlertDialogTitle>
                <AlertDialogDescription>
                  Your download is not complete. Would you like to cancel the download?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={handleConfirmCancel}>Cancel download</AlertDialogCancel>
                <AlertDialogAction onClick={handleContinueDownload}>Continue download</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Job list */}
      {menuState === "expanded" ? (
        <div className="p-2 overflow-y-auto max-h-80">
          <ul className="space-y-2">
            {jobs.map((job) => (
              <li key={job.id}>
                <JobStatusItem job={job} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>,
    portalContainer,
  );
}
